import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDataStore } from '@/stores/dataStore';
import { api } from '@/lib/api';
import { Button } from '@/components/Button';
import { Modal, TextField, Label, Input, Alert, Tabs } from '@heroui/react';
import { CloseIcon, PlusIcon } from '@/components/icons';
import { Tooltip } from '@/components/Tooltip';
import { fileToIconDataUrl, ImageUploadError } from '@/lib/imageUpload';
import { humanizeApiError } from '@/lib/humanizeApiError';
import { toast } from '@/lib/toast';

interface InvitePreview {
  code: string;
  guild: { id: string; name: string; iconUrl: string | null };
  expiresAt: string | null;
  uses: number;
  maxUses: number | null;
  memberCount: number;
}

/**
 * Modal for adding a server: create a new one OR join via invite code.
 *
 * Design intent: NOT a Discord clone. We don't push pre-made templates
 * ("Gaming server" / "School club" etc). Just two clean tabs, clean
 * inputs, no decorative outlines. Backdrop click + Esc close.
 */
export function ServerModal(props: { open: boolean; onClose: () => void; initialTab?: 'create' | 'join' }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'create' | 'join'>('create');

  // Open on the requested tab whenever the modal reopens (defaults to create).
  useEffect(() => {
    if (props.open) setTab(props.initialTab ?? 'create');
  }, [props.open, props.initialTab]);

  return (
    <Modal.Backdrop className="app-viewport-overlay" isOpen={props.open} onOpenChange={(open) => { if (!open) props.onClose(); }}>
      <Modal.Container size="md" scroll="inside" className="app-panel-container app-server-flow-container">
        <Modal.Dialog className="app-panel-dialog app-server-flow-dialog">
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>{t('srv.modalServer')}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>
        <Tabs
          selectedKey={tab}
          onSelectionChange={(k) => setTab(k as 'create' | 'join')}
        >
          <Tabs.ListContainer>
            <Tabs.List aria-label={t('srv.createOrJoin')}>
              <Tabs.Tab id="create">{t('srv.create')}<Tabs.Indicator /></Tabs.Tab>
              <Tabs.Tab id="join">{t('srv.join')}<Tabs.Indicator /></Tabs.Tab>
            </Tabs.List>
          </Tabs.ListContainer>
          <Tabs.Panel id="create" className="pt-4">
            <CreateForm onClose={props.onClose} />
          </Tabs.Panel>
          <Tabs.Panel id="join" className="pt-4">
            <JoinForm onClose={props.onClose} />
          </Tabs.Panel>
        </Tabs>
        <RestorableGuilds onClose={props.onClose} />
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function CreateForm(props: { onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const upsertGuild = useDataStore((s) => s.upsertGuild);
  const selectGuild = useDataStore((s) => s.selectGuild);

  const previewName = name.trim() || t('srv.defaultServerName');
  const initial = previewName.charAt(0).toUpperCase();

  async function pickIcon(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      const dataUrl = await fileToIconDataUrl(file);
      setIconUrl(dataUrl);
    } catch (err) {
      setUploadError(
        err instanceof ImageUploadError
          ? err.message
          : t('srv.iconProcessFailed'),
      );
    } finally {
      setUploading(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const g = await api.createGuild({
        name: name.trim(),
        ...(iconUrl.trim() ? { iconUrl: iconUrl.trim() } : {}),
      });
      upsertGuild(g);
      selectGuild(g.id);
      props.onClose();
    } catch (err) {
      // Surface a humanized title; the inline error banner shows the
      // hint where applicable so the user sees actionable copy instead
      // of a raw "fetch failed".
      const h = humanizeApiError(err, t('srv.createGuildFailed'));
      setError(h.hint ? `${h.title}. ${h.hint}` : h.title);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="app-server-flow-form flex flex-col gap-4">
      <section className="app-server-create-identity flex items-center gap-3">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={(e) => void pickIcon(e)}
        />
        <div className="relative shrink-0">
          <Tooltip label={iconUrl ? t('srv.iconChangeAlt') : t('srv.iconUpload')}>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || busy}
            aria-label={iconUrl ? t('srv.iconChangeAlt') : t('srv.iconUpload')}
            className="group relative grid h-14 w-14 place-items-center overflow-hidden rounded-xl bg-default text-xl font-medium text-ink-muted transition-colors hover:bg-default/70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-60"
          >
            {iconUrl ? (
              <img src={iconUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              initial
            )}
          </button>
          </Tooltip>
          {!iconUrl && (
            <span aria-hidden className="pointer-events-none absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-lg bg-default text-ink">
              <PlusIcon className="h-3 w-3" />
            </span>
          )}
          {iconUrl && (
            <Tooltip label={t('srv.iconRemove')}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIconUrl('');
              }}
              aria-label={t('srv.iconRemove')}
              className="absolute -bottom-1 -right-1 grid h-6 w-6 place-items-center rounded-lg bg-danger text-white transition-colors hover:bg-danger"
            >
              <CloseIcon className="h-3 w-3" />
            </button>
            </Tooltip>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm text-ink">{t(uploading ? 'srv.iconUploading' : 'srv.iconUpload')}</p>
          <p className="app-server-icon-hint text-xs text-ink-muted">{t('srv.iconHint')}</p>
        </div>
      </section>
      <div className="app-server-create-name min-w-0">
        <TextField isRequired fullWidth value={name} onChange={setName}>
          <Label>{t('srv.serverName')}</Label>
          <Input variant="secondary" autoFocus placeholder={t('srv.serverNamePlaceholder')} maxLength={100} />
        </TextField>
      </div>
      {uploadError && (
        <Alert status="danger">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>{uploadError}</Alert.Title>
          </Alert.Content>
        </Alert>
      )}
      {error && (
        <Alert status="danger">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>{error}</Alert.Title>
          </Alert.Content>
        </Alert>
      )}
      <FormActions
        onCancel={props.onClose}
        submitLabel={busy ? t('srv.creating') : t('srv.createServer')}
        disabled={!name.trim() || busy}
      />
    </form>
  );
}

function JoinForm(props: { onClose: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectGuild = useDataStore((s) => s.selectGuild);

  // Live preview after the user finishes typing (debounced).
  useEffect(() => {
    const trimmed = extractCode(code);
    if (!trimmed) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const timerId = setTimeout(async () => {
      setPreviewing(true);
      setError(null);
      try {
        const p = await api.previewInvite(trimmed);
        if (cancelled) return;
        setPreview(p);
      } catch (err) {
        if (cancelled) return;
        setPreview(null);
        // ignore — preview is best-effort, errors surface on accept
        void err;
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [code]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = extractCode(code);
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.acceptInvite(trimmed);
      // After accept, fetch the guild so it lands in our store.
      const guild = await api.getGuild(res.guildId);
      // upsertGuild via store: getGuild returns GuildDetail which includes channels.
      useDataStore.getState().upsertGuild(guild);
      selectGuild(guild.id);
      props.onClose();
    } catch (err) {
      const h = humanizeApiError(err, t('srv.joinFailed'));
      setError(h.hint ? `${h.title}. ${h.hint}` : h.title);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="app-server-flow-form flex flex-col gap-4">
      <TextField isRequired fullWidth value={code} onChange={setCode}>
        <Label>{t('srv.inviteCodeLabel')}</Label>
        <Input variant="secondary" autoFocus placeholder={t('srv.inviteCodePlaceholder')} className="font-mono" />
      </TextField>
      <PreviewCard preview={preview} loading={previewing} />
      {error && (
        <Alert status="danger">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>{error}</Alert.Title>
          </Alert.Content>
        </Alert>
      )}
      <FormActions
        onCancel={props.onClose}
        submitLabel={busy ? t('srv.joining') : t('srv.join')}
        disabled={!extractCode(code) || busy}
      />
    </form>
  );
}

function PreviewCard(props: { preview: InvitePreview | null; loading: boolean }) {
  const { t } = useTranslation();
  if (!props.preview && !props.loading) return null;
  if (props.loading) {
    return (
      <div
        className="rounded-xl px-3 py-2.5 text-xs text-ink-muted"
        style={{
          backgroundColor: 'rgb(var(--surface-input) / calc(var(--surface-input-alpha) * 0.4))',
        }}
      >
        {t('srv.checkingInvite')}
      </div>
    );
  }
  if (!props.preview) return null;
  const g = props.preview.guild;
  const initial = g.name.charAt(0).toUpperCase();
  const usesText =
    props.preview.maxUses === null || props.preview.maxUses === undefined
      ? t('srv.previewUsesUnlimited', { uses: props.preview.uses })
      : t('srv.previewUsesLimited', { uses: props.preview.uses, max: props.preview.maxUses });
  return (
    <div
      className="flex items-center gap-3 rounded-xl p-3"
      style={{
        backgroundColor: 'rgb(var(--surface-input) / calc(var(--surface-input-alpha) * 0.4))',
      }}
    >
      <div
        className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-xl bg-default text-sm font-medium text-ink-muted"
      >
        {g.iconUrl ? (
          <img src={g.iconUrl} alt="" className="h-full w-full rounded-xl object-cover" />
        ) : (
          initial
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-ink">{g.name}</div>
        <div className="truncate text-[11px] tabular-nums text-ink-muted">
          {t('srv.inviteMemberCount', { count: props.preview.memberCount })} · {usesText}
          {props.preview.expiresAt && (
            <> · {t('srv.previewExpires', { date: new Date(props.preview.expiresAt).toLocaleString('ru-RU') })}</>
          )}
        </div>
      </div>
    </div>
  );
}

function FormActions(props: {
  onCancel: () => void;
  submitLabel: string;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="app-server-flow-actions flex items-center justify-end gap-2 pt-1">
      <Button variant="ghost" size="sm" onClick={props.onCancel}>
        {t('srv.cancel')}
      </Button>
      <Button variant="primary" size="sm" type="submit" disabled={props.disabled}>
        {props.submitLabel}
      </Button>
    </div>
  );
}

/**
 * Pull a code out of either a raw code or a /invite/<code> URL.
 * Trims surrounding whitespace.
 */
function extractCode(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  const urlMatch = s.match(/\/invite\/([A-Za-z0-9]+)/);
  if (urlMatch) return urlMatch[1] ?? null;
  // Bare code: alphanumeric, server generates 8-char codes. Accept 4-32.
  if (/^[A-Za-z0-9]{4,32}$/.test(s)) return s;
  return null;
}

interface DeletedGuild {
  id: string;
  name: string;
  iconUrl: string | null;
  deletedAt: string;
}

const RESTORE_GRACE_DAYS = 30;

/**
 * "Recently deleted" restore list. Soft-deleted servers the user owns that are
 * still inside the grace window can be brought back in one click. Renders
 * nothing when there's nothing to restore, so the modal stays clean.
 */
function RestorableGuilds(props: { onClose: () => void }) {
  const { t } = useTranslation();
  const [guilds, setGuilds] = useState<DeletedGuild[]>([]);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    api
      .listDeletedGuilds()
      .then((res) => {
        if (!cancelled) setGuilds(Array.isArray(res?.guilds) ? res.guilds : []);
      })
      .catch(() => {
        // Don't swallow: an owner must not silently think their deleted
        // server is gone for good. Surface an inline retry instead.
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  async function restore(id: string) {
    if (restoringId) return;
    setRestoringId(id);
    setRestoreError(null);
    const name = guilds.find((g) => g.id === id)?.name ?? '';
    try {
      await api.restoreGuild(id);
    } catch (err) {
      setRestoringId(null);
      const status = (err as { status?: number } | null)?.status;
      // Gone for good (410: the grace ran out) or already back (409): the row
      // no longer belongs in «recently deleted». Anything else — offline, a
      // server error — keeps it so the owner can try again.
      if (status === 410 || status === 409) setGuilds((cur) => cur.filter((g) => g.id !== id));
      if (status === 409) return;
      setRestoreError(
        status === 410
          ? t('srv.restoreExpired', { name })
          : humanizeApiError(err, t('srv.restoreFailed')).title,
      );
      return;
    }
    // Restored. Load it and go there; if that fetch fails the restore still
    // stands (the server arrives over the socket), so say so and go anyway.
    try {
      useDataStore.getState().upsertGuild(await api.getGuild(id));
    } catch {
      toast.success(t('srv.restored', { name }));
    }
    useDataStore.getState().selectGuild(id);
    props.onClose();
  }

  if (failed) {
    return (
      <div className="mt-5 flex items-center gap-2 border-t border-separator pt-4 text-[11px] text-ink-muted">
        <span>{t('srv.recentlyDeletedError')}</span>
        <button
          type="button"
          className="text-accent underline"
          onClick={() => setReloadKey((k) => k + 1)}
        >
          {t('srv.refresh')}
        </button>
      </div>
    );
  }

  if (guilds.length === 0) {
    // The last row may just have gone (grace expired): still say why.
    return restoreError ? (
      <p role="alert" className="mt-5 text-[11px] text-danger">
        {restoreError}
      </p>
    ) : null;
  }

  return (
    <div className="mt-5 border-t border-separator pt-4">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
        {t('srv.recentlyDeleted')}
      </div>
      <div className="flex flex-col gap-2">
        {guilds.map((g) => {
          const daysLeft = Math.max(
            0,
            Math.ceil(
              (new Date(g.deletedAt).getTime() + RESTORE_GRACE_DAYS * 86400000 - Date.now()) /
                86400000,
            ),
          );
          return (
            <div key={g.id} className="flex items-center gap-3">
              <div
                className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg text-xs font-light text-accent"
                style={{ backgroundColor: 'rgb(var(--bk-accent) / 0.10)' }}
              >
                {g.iconUrl ? (
                  <img src={g.iconUrl} alt="" className="h-full w-full rounded-lg object-cover" />
                ) : (
                  g.name.charAt(0).toUpperCase()
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-ink">{g.name}</div>
                <div className="text-[11px] text-ink-muted">
                  {t('srv.restoreWithin', { count: daysLeft })}
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                disabled={restoringId === g.id}
                onClick={() => void restore(g.id)}
              >
                {restoringId === g.id ? t('srv.restoring') : t('srv.restore')}
              </Button>
            </div>
          );
        })}
      </div>
      {restoreError && (
        <p role="alert" className="mt-2 text-[11px] text-danger">
          {restoreError}
        </p>
      )}
    </div>
  );
}
